import { StyleSheet } from 'aphrodite';
import { SPACE, THEME } from '../../../components-v2/Theme/theme';
import { darkGrayFontColor } from '../../../styles/colors';

const { colors, common, typography } = THEME;

export const styleSheet = StyleSheet.create({
	card: {
		boxSizing: 'border-box',
		flexDirection: 'column',
		gap: SPACE[12],
		width: '100%',
		':has(*) *': {
			boxSizing: 'border-box',
		},
	},
	cardHeader: {
		alignItems: 'center',
		display: 'flex',
		gap: SPACE[8],
	},
	statusDot: {
		backgroundColor: colors.success.main,
		borderRadius: '50%',
		flexShrink: 0,
		height: 8,
		width: 8,
	},
	tiles: {
		display: 'grid',
		gap: SPACE[12],
		gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
	},
	tile: {
		borderColor: colors.border.light,
		gap: SPACE[12],
		height: 'auto',
		justifyContent: 'flex-start',
		minHeight: 64,
		padding: SPACE[12],
		textAlign: 'left',
		whiteSpace: 'normal',
		width: '100%',
		':is([aria-expanded="true"])': {
			backgroundColor: colors.background.light,
		},
	},
	tileOpen: {
		borderColor: colors.primary.main,
		boxShadow: `inset 0 0 0 1px ${colors.primary.main}`,
	},
	tileIcon: {
		alignItems: 'center',
		borderRadius: common.borderRadius,
		display: 'flex',
		flexShrink: 0,
		height: 36,
		justifyContent: 'center',
		width: 36,
		':has(*) > svg': {
			height: 20,
			width: 20,
		},
	},
	emailExecIcon: {
		backgroundColor: colors.pill.blue.background,
		color: colors.pill.blue.icon,
	},
	meetingPrepIcon: {
		backgroundColor: colors.pill.purple.background,
		color: colors.pill.purple.icon,
	},
	keepInTouchIcon: {
		backgroundColor: colors.pill.teal.background,
		color: colors.pill.teal.icon,
	},
	tileText: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: 2,
		minWidth: 0,
	},
	tileHeadline: {
		color: colors.text.main,
		fontSize: typography.fontSizes.sm,
		lineHeight: '20px',
	},
	tileCount: {
		fontFamily: typography.fontFamilies.sora,
		fontSize: typography.fontSizes.lg,
		fontWeight: typography.fontWeights.bold,
	},
	tileError: {
		color: colors.danger.main,
	},
	tileName: {
		color: darkGrayFontColor,
		fontSize: typography.fontSizes.xs,
	},
	tileChevron: {
		color: darkGrayFontColor,
	},
	tileSkeleton: {
		alignItems: 'center',
		border: `1px solid ${colors.border.light}`,
		borderRadius: common.borderRadius,
		display: 'flex',
		gap: SPACE[12],
		minHeight: 64,
		padding: SPACE[12],
	},
	skeletonIcon: {
		borderRadius: common.borderRadius,
		flexShrink: 0,
		height: 36,
		width: 36,
	},
	skeletonText: {
		height: 16,
		width: '60%',
	},
	panel: {
		borderTop: `1px solid ${colors.border.light}`,
		// Truncated lines still report their full width as min-content, which would stretch the dashboard column.
		contain: 'inline-size',
		display: 'flex',
		flexDirection: 'column',
		paddingTop: SPACE[8],
	},
	panelSource: {
		color: darkGrayFontColor,
		fontSize: typography.fontSizes.xs,
		margin: 0,
	},
	panelNote: {
		color: darkGrayFontColor,
		fontSize: typography.fontSizes.sm,
		margin: `${SPACE[8]}px 0 0`,
	},
	failureNote: {
		color: colors.danger.main,
		textDecoration: 'none',
		':hover': {
			textDecoration: 'underline',
		},
	},
	panelItem: {
		alignItems: 'center',
		borderBottom: `1px solid ${colors.border.light}`,
		display: 'flex',
		gap: SPACE[16],
		padding: `${SPACE[12]}px 0`,
	},
	panelItemLead: {
		flexShrink: 0,
	},
	statusLead: {
		alignItems: 'center',
		display: 'flex',
		gap: SPACE[8],
		width: 120,
	},
	kindDot: {
		borderRadius: '50%',
		flexShrink: 0,
		height: 8,
		width: 8,
	},
	draftDot: {
		backgroundColor: colors.pill.blue.icon,
	},
	awaitingDot: {
		backgroundColor: colors.pill.orange.icon,
	},
	riskDot: {
		backgroundColor: colors.pill.red.icon,
	},
	actionDot: {
		backgroundColor: colors.pill.gray.icon,
	},
	statusLabel: {
		color: darkGrayFontColor,
		fontSize: typography.fontSizes.xs,
		whiteSpace: 'nowrap',
	},
	timeLead: {
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
		width: 80,
	},
	timeText: {
		color: colors.text.main,
		fontFamily: typography.fontFamilies.sora,
		fontSize: typography.fontSizes.sm,
		fontVariantNumeric: 'tabular-nums',
		fontWeight: typography.fontWeights.bold,
	},
	timePast: {
		color: darkGrayFontColor,
		fontWeight: typography.fontWeights.standard,
	},
	timeLength: {
		color: darkGrayFontColor,
		fontSize: typography.fontSizes.xs,
	},
	panelItemBody: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: 2,
		minWidth: 0,
	},
	panelItemTitle: {
		...common.truncate,
		color: colors.text.main,
		fontSize: typography.fontSizes.sm,
		fontWeight: typography.fontWeights.bold,
	},
	panelItemMeta: {
		color: darkGrayFontColor,
		fontWeight: typography.fontWeights.standard,
	},
	panelItemDetail: {
		...common.truncate,
		color: darkGrayFontColor,
		fontSize: typography.fontSizes.sm,
	},
	panelMessage: {
		alignItems: 'center',
		display: 'flex',
		gap: SPACE[16],
		justifyContent: 'space-between',
		paddingTop: SPACE[8],
	},
	panelMessageText: {
		color: darkGrayFontColor,
		fontSize: typography.fontSizes.sm,
		margin: 0,
	},
	panelExcerpt: {
		...common.truncate,
		color: darkGrayFontColor,
		fontSize: typography.fontSizes.sm,
		margin: 0,
		paddingTop: SPACE[8],
	},
	panelFooter: {
		alignItems: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		gap: SPACE[16],
		paddingTop: SPACE[8],
	},
	footerLink: {
		paddingLeft: 0,
		paddingRight: 0,
	},
	panelSkeletonRow: {
		height: 16,
		margin: `${SPACE[12]}px 0`,
		width: '70%',
	},
});
